import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronRight, FolderKanban, Star } from 'lucide-react';
import type { Project } from '@/types';
import { palette } from '@/styles/designSystem';
import { cn } from '@/utils/cn';
import {
  readWorkspaceNavigation,
  recordRecentProject,
  toggleFavoriteProject,
  writeWorkspaceNavigation,
} from '@/utils/workspaceNavigation';

interface WorkspaceTreeProps {
  projects: Project[];
  userId?: string;
  isLoading?: boolean;
  compact?: boolean;
}

interface ProjectGroup {
  id: string;
  name: string;
  projects: Project[];
}

function ProjectLink({
  project,
  isFavorite,
  onToggleFavorite,
}: {
  project: Project;
  isFavorite: boolean;
  onToggleFavorite: () => void;
}) {
  return (
    <div className="group flex min-w-0 items-center gap-1">
      <NavLink
        to={`/projects/${project.id}`}
        className={({ isActive }) =>
          cn(
            'flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-xl px-3 text-[15px] transition-colors',
            isActive
              ? 'bg-primary-300 font-medium text-gray-900'
              : 'text-gray-600 hover:bg-canvas-cream hover:text-gray-900',
          )
        }
      >
        <span
          className="h-2.5 w-2.5 flex-none rounded-sm"
          style={{ backgroundColor: project.color || palette.inkMute2 }}
          aria-hidden="true"
        />
        <span className="truncate">{project.name}</span>
      </NavLink>
      <button
        type="button"
        className={cn(
          'flex h-9 w-9 flex-none items-center justify-center rounded-lg transition-colors hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-primary-400',
          isFavorite
            ? 'text-amber-500'
            : 'text-gray-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus:opacity-100',
        )}
        aria-label={`${isFavorite ? 'Remove' : 'Add'} ${project.name} ${isFavorite ? 'from' : 'to'} favorites`}
        aria-pressed={isFavorite}
        onClick={onToggleFavorite}
      >
        <Star className="h-4 w-4" fill={isFavorite ? 'currentColor' : 'none'} aria-hidden="true" />
      </button>
    </div>
  );
}

export default function WorkspaceTree({
  projects,
  userId,
  isLoading,
  compact,
}: WorkspaceTreeProps) {
  const location = useLocation();
  const [preferences, setPreferences] = useState(() => readWorkspaceNavigation(userId));
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  useEffect(() => {
    setPreferences(readWorkspaceNavigation(userId));
    setExpandedGroups(new Set());
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const match = location.pathname.match(/^\/projects\/([^/]+)/);
    const projectId = match?.[1];
    if (!projectId || !projects.some((project) => project.id === projectId)) return;

    setPreferences((current) => {
      const next = recordRecentProject(current, projectId);
      writeWorkspaceNavigation(userId, next);
      return next;
    });
  }, [location.pathname, projects, userId]);

  const groups = useMemo<ProjectGroup[]>(() => {
    const grouped = new Map<string, ProjectGroup>();
    for (const project of projects) {
      const id = project.team_id || 'workspace';
      const existing = grouped.get(id) ?? {
        id,
        name: project.team_name || 'Workspace projects',
        projects: [],
      };
      existing.projects.push(project);
      grouped.set(id, existing);
    }
    return [...grouped.values()]
      .map((group) => ({
        ...group,
        projects: group.projects.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  const projectById = useMemo(
    () => new Map(projects.map((project) => [project.id, project])),
    [projects],
  );
  const favoriteProjects = preferences.favoriteProjectIds
    .map((id) => projectById.get(id))
    .filter((project): project is Project => Boolean(project));
  const toggleFavorite = (projectId: string) => {
    if (!userId) return;
    setPreferences((current) => {
      const next = toggleFavoriteProject(current, projectId);
      writeWorkspaceNavigation(userId, next);
      return next;
    });
  };

  const renderProject = (project: Project) => (
    <ProjectLink
      key={project.id}
      project={project}
      isFavorite={preferences.favoriteProjectIds.includes(project.id)}
      onToggleFavorite={() => toggleFavorite(project.id)}
    />
  );

  if (compact) {
    return (
      <NavLink
        to="/projects"
        className={({ isActive }) =>
          cn(isActive ? 'sidebar-link-active' : 'sidebar-link', 'justify-center px-0')
        }
        title="Projects"
        aria-label="Projects"
      >
        <FolderKanban className="h-5 w-5" strokeWidth={1.5} />
      </NavLink>
    );
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between px-2">
        <p className="sidebar-section-header !mb-0 !px-0">Spaces</p>
        <NavLink
          to="/projects"
          className="text-xs font-medium text-primary-600 hover:text-primary-700"
        >
          View all
        </NavLink>
      </div>

      {favoriteProjects.length > 0 && (
        <div className="mb-3">
          <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            Favorites
          </p>
          <div className="space-y-0.5">{favoriteProjects.map(renderProject)}</div>
        </div>
      )}

      {isLoading ? (
        <p className="px-2 py-2 text-xs text-gray-400">Loading projects…</p>
      ) : groups.length === 0 ? (
        <NavLink to="/projects" className="sidebar-link">
          <FolderKanban className="h-5 w-5" strokeWidth={1.5} />
          <span>Projects</span>
        </NavLink>
      ) : (
        <div className="space-y-1">
          {groups.map((group) => {
            const isCollapsed = !expandedGroups.has(group.id);
            return (
              <div key={group.id}>
                <button
                  type="button"
                  className="flex min-h-9 w-full items-center gap-2 rounded-lg px-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100"
                  aria-expanded={!isCollapsed}
                  onClick={() =>
                    setExpandedGroups((current) => {
                      const next = new Set(current);
                      if (next.has(group.id)) next.delete(group.id);
                      else next.add(group.id);
                      return next;
                    })
                  }
                >
                  <span
                    className="flex h-5 w-5 flex-none items-center justify-center rounded bg-hairline text-[10px] text-gray-700"
                    aria-hidden="true"
                  >
                    {group.name.charAt(0).toUpperCase()}
                  </span>
                  <span className="truncate">{group.name}</span>
                  {isCollapsed ? (
                    <ChevronRight
                      className="ml-auto h-4 w-4 flex-none text-gray-400"
                      aria-hidden="true"
                    />
                  ) : (
                    <ChevronDown
                      className="ml-auto h-4 w-4 flex-none text-gray-400"
                      aria-hidden="true"
                    />
                  )}
                </button>
                {!isCollapsed && <div className="ml-2">{group.projects.map(renderProject)}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
